import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge, message, Empty } from 'antd';
import { useMutation, useLazyQuery, useReactiveVar } from '@apollo/client';

import PropTypes from 'prop-types';

import { getFilteredSections } from 'helpers/contentlibrary';
import { GET_ALL_SECTIONS_LIBRARIES } from 'graphql/queries/contentLibraryQueries';
import {
  UPDATE_CONTENT_LIBRARY,
  CONTENT_LIBRARY_SECTION_DELETE,
} from 'graphql/mutations/contentLibraryMutations';
import { SECTIONS_LIMIT } from 'constants/index';
import { sectionsVar } from 'graphql/cache';
import { fontListEnglish, fontListHebrew } from 'pages/Proposal/constants/constants';
import { fontLoadedVar } from 'graphql/cache';
import { loadProposalFont } from 'helpers/proposal';
import { ContentLoader } from '../ContentLoader/ContentLoader';
import { useContentLibrary } from 'context/ContentLibraryContext';

import ConfirmModal from 'components/ConfirmModal';
import ContentLibraryTree from '../ContentLibraryTree/ContentLibraryTree';
import SectionCard from '../SectionCard/SectionCard';
import PreviewProposalModal from '../PreviewProposalModal/PreviewProposalModal';

/**
 * Section tab
 *
 * @param {Object} props
 * @param {Object} props.user
 * @param {string} props.searchValue
 * @param {Object} props.config
 * @param {boolean} props.isLocked
 * @param {Function} props.loadConfig
 * @param {string} props.language
 */
const SectionTab = ({ user, searchValue, config, isLocked, loadConfig, language }) => {
  // Get treeData and related functions from context
  const { treeData, selectedFolderKey, onContentLibraryKeySelect } = useContentLibrary();

  const [previewModal, showPreviewModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [confirmModal, showConfirmModal] = useState(false);
  const [selectedSectionEditValue, setSelectedSectionEditValue] = useState('');
  const fontLoaded = useReactiveVar(fontLoadedVar);

  const allSectionCursorRef = useRef(null);
  const isLoadingMoreRef = useRef(false);

  /**
   * Handle prelist load
   *
   * @param {string} langChange
   */
  const handlePrelistLoad = (langChange) => {
    let prelist = [];

    if (language?.toLowerCase() === 'hebrew' || langChange === 'hebrew') {
      prelist = fontListHebrew;
    } else {
      prelist = fontListEnglish;
    }

    if (!fontLoaded) {
      fontLoadedVar(true);
      loadProposalFont(prelist, langChange || language, user);
    }
  };

  /**
   * Sections
   */
  const sections = useReactiveVar(sectionsVar || []);

  /**
   * History
   */
  const history = useHistory();

  /**
   * Process fetched data
   *
   * @param {Object} data
   * @param {boolean} append
   */
  const processFetchedData = useCallback(
    (data, append = false) => {
      if (!data?.fetchContentLibrarySectionsItems) {
        return;
      }

      const { sections: newSections, pagingMetadata } = data.fetchContentLibrarySectionsItems;

      if (!newSections) {
        return;
      }

      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;

      if (append) {
        const oldSections = sections || [];

        // remove duplicates
        const existingIds = new Set(oldSections.map((item) => item._id));
        const filteredNewSections = [];

        for (const item of newSections) {
          if (!existingIds.has(item._id)) {
            existingIds.add(item._id);
            filteredNewSections.push(item);
          }
        }

        sectionsVar([...oldSections, ...filteredNewSections]);

        if (filteredNewSections.length > 0) {
          allSectionCursorRef.current = pagingMetadata;
        }
      } else {
        sectionsVar(newSections);
        allSectionCursorRef.current = pagingMetadata;
      }
    },
    [sections]
  );

  /**
   * Fetch all sections
   */
  const [fetchAllSections, { loading: isFetchingSections, refetch, fetchMore }] = useLazyQuery(
    GET_ALL_SECTIONS_LIBRARIES,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => processFetchedData(data),
    }
  );

  /**
   * Use effect
   */
  useEffect(() => {
    if (!sections.length) {
      fetchAllSections({
        variables: {
          limit: SECTIONS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
          cursorPaging: null,
          search: searchValue,
          key: selectedFolderKey,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Use effect
   */
  useEffect(() => {
    handlePrelistLoad(language?.toLowerCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Refetch sections
   *
   * @param {Object} params
   * @param {string} params.key
   * @param {string} params.search
   */
  const refetchSections = useCallback(
    async ({ key, search }) => {
      if (user?.paymentStatus?.canLock) {
        return;
      }
      const { data } = await refetch?.({
        limit: SECTIONS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
        cursorPaging: null,
        search,
        key,
      });
      processFetchedData(data);
    },
    [refetch, user?.paymentStatus?.canLock, processFetchedData]
  );

  /**
   * Fetch more sections
   */
  const fetchMoreSections = useCallback(async () => {
    const allSectionCursor = allSectionCursorRef.current;
    if (!allSectionCursor?.hasNext || isLoadingMoreRef.current) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try{
      const { data } = await fetchMore({
        variables: {
          limit: SECTIONS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
          cursorPaging: allSectionCursor?.cursor ?? null,
          search: searchValue,
          key: selectedFolderKey,
        },
      });
  
      processFetchedData(data, true);
    }
    catch(error){
      console.error('Error fetching more sections:', error);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, selectedFolderKey, fetchMore, processFetchedData]);

  /**
   * Use effect
   */
  useEffect(() => {
    const mainContent = document.querySelector('.section-card-container');
    if (!mainContent) {
      return;
    }
    async function onScroll() {
      if (isLoadingMoreRef.current) {
        return;
      }
      if (mainContent?.scrollTop + mainContent?.offsetHeight + 1 > mainContent?.scrollHeight) {
        fetchMoreSections();
      }
    }

    mainContent.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      mainContent.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMoreSections]);

  /**
   * Update content item mutation
   */
  const [updateContentItem] = useMutation(UPDATE_CONTENT_LIBRARY, {
    onCompleted() {
      refetchSections({
        key: selectedFolderKey,
        search: searchValue,
      });
    },
  });

  /**
   * Delete section mutation
   */
  const [deleteSection] = useMutation(CONTENT_LIBRARY_SECTION_DELETE, {
    onCompleted() {
      let tempSection = sections.filter((section) => section._id !== selectedSectionId);
      sectionsVar(tempSection);
      setSelectedSectionId('');
      message.success('Section deleted successfully');
      showConfirmModal(false);
    },
    onError() {
      message.error('Section deletion failed');
      showConfirmModal(false);
    },
  });

  /**
   * Use effect
   */
  useEffect(() => {
    if (searchValue || user.deletedTemplates) {
      refetchSections({
        key: selectedFolderKey,
        search: searchValue,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, selectedFolderKey]);

  /**
   * Filtered sections
   */
  const filteredSections = getFilteredSections({
    sections,
    searchValue,
  });

  /**
   * Starred items
   */
  const { starred: starredItems, all: allItems } = filteredSections;

  /**
   * Star or unstar
   *
   * @param {Object} params
   * @param {boolean} params.starred
   * @param {string} params.sectionId
   */
  const starOrUnstar = ({ starred, sectionId }) => {
    updateContentItem({
      variables: {
        id: sectionId,
        type: 'sections',
        item: { starred },
      },
    });
  };

  /**
   * Confirm delete section
   */
  const confirmDeleteSection = () => {
    deleteSection({
      variables: {
        _id: selectedSectionId,
        folderKey: selectedFolderKey,
      },
    });
  };

  /**
   * On click menu item
   *
   * @param {Object} params
   * @param {string} params.actionType
   * @param {string} params.sectionId
   * @param {boolean} params.starred
   * @param {Object} params.sectionData
   * @param {string} params.sectionName
   */
  const onClickMenuItem = async ({ actionType, sectionId, starred, sectionData, sectionName }) => {
    if (actionType === 'delete') {
      setSelectedSectionId(sectionId);
      showConfirmModal(true);
    } else if (actionType === 'starOrUnstar') {
      starOrUnstar({ starred, sectionId });
    } else if (actionType === 'preview') {
      setSelectedSection(sectionData);
      showPreviewModal(true);
    } else if (actionType === 'edit_section') {
      history.push(`/s/${sectionId}`);
    } else if (actionType === 'rename') {
      setSelectedSectionId(sectionId);
      setSelectedSectionEditValue(sectionName);
    } else if (actionType === 'save') {
      await updateContentItem({
        variables: {
          id: sectionId,
          type: 'sections',
          item: { libraryTitle: selectedSectionEditValue },
        },
      });
      setSelectedSectionId('');
      setSelectedSectionEditValue('');
    } else {
      message.warning('Coming soon 😊');
    }
  };

  /**
   * Is sections empty
   */
  const isSectionsEmpty = !(starredItems.length || allItems.length);

  /**
   * Filtered starred items
   */
  const filteredStarredItems = starredItems.filter((item) => !item.isHeader);

  /**
   * Filtered all items
   */
  const filteredAllItems = allItems.filter((item) => !item.isHeader);

  /**
   * On key select
   */
  const onKeySelect = (selectedFolderKey) => {
    onContentLibraryKeySelect(selectedFolderKey);
  };

  /**
   * Render
   */
  return (
    <div className="content-library-item content-library-item-section">
      <ContentLibraryTree
        onKeySelect={onKeySelect}
        type="sections"
        onUpdate={() =>
          refetchSections({
            key: selectedFolderKey,
            search: searchValue,
          })
        }
        userId={user && user._id}
        userRole={user?.roles && user?.roles[0]}
        isLocked={isLocked}
        treeData={treeData}
      />
      <div className="split-d2 section-card-container">
        {isFetchingSections ? (
          <ContentLoader />
        ) : (
          <>
            {filteredStarredItems.length ? (
              <div className="stared-header">
                MY STARRED SECTIONS
                <Badge className="star-badge" count={starredItems.length} />
              </div>
            ) : null}

            <div className="section-scroll-wrapper">
              <React.Fragment>
                {isSectionsEmpty && (
                  <div className="item-row empty-wrapper">
                    <Empty
                      className="empty-view"
                      description="Looks like you have no section files yet"
                    />
                  </div>
                )}
                <div className="item-row covers-card">
                  {filteredStarredItems.map((item) => {
                    return (
                      <SectionCard
                        onDragStart={(ev) => {
                          ev.dataTransfer.setData('data', JSON.stringify(item));
                        }}
                        item={item}
                        title={item.libraryTitle}
                        key={item._id}
                        description={item.text || item.title}
                        onClickMenuItem={onClickMenuItem}
                        sectionId={item._id}
                        isStarred
                        userId={user._id}
                        userRole={user?.roles && user?.roles[0]}
                        sectionUserId={item.uid}
                        sectionAuid={item.auid}
                        itemEditable={selectedSectionId === item._id}
                        setEditValue={setSelectedSectionEditValue}
                      />
                    );
                  })}
                </div>

                {filteredStarredItems.length ? <div className="divider-line" /> : null}

                <div className="item-row covers-card">
                  {filteredAllItems.map((item) => {
                    return (
                      <SectionCard
                        onDragStart={(ev) => {
                          ev.dataTransfer.setData('data', JSON.stringify(item));
                        }}
                        item={item}
                        title={item.libraryTitle}
                        key={item._id}
                        description={item.text || item.title}
                        sectionId={item._id}
                        userId={user._id}
                        userRole={user?.roles && user?.roles[0]}
                        onClickMenuItem={onClickMenuItem}
                        sectionUserId={item.uid}
                        sectionAuid={item.auid}
                        itemEditable={selectedSectionId === item._id}
                        setEditValue={setSelectedSectionEditValue}
                      />
                    );
                  })}
                </div>

                {isLoadingMore && <ContentLoader length={4} />}
              </React.Fragment>
            </div>
          </>
        )}
      </div>
      {previewModal && (
        <PreviewProposalModal
          onCancel={() => showPreviewModal(false)}
          section={selectedSection}
          config={config}
          loadConfig={loadConfig}
          user={user}
          title={selectedSection.libraryTitle}
        />
      )}
      {confirmModal && (
        <ConfirmModal
          title="Delete Section?"
          body="This action cannot be undone"
          visible={confirmModal}
          cancelText="CANCEL"
          confirmText="YES, DELETE"
          onCancel={() => showConfirmModal(false)}
          onConfirm={confirmDeleteSection}
        />
      )}
    </div>
  );
};

/**
 * Default props
 */
SectionTab.defaultProps = {
  sections: [],
  searchValue: '',
  config: {},
  isLocked: false,
};

/**
 * Prop types
 */
SectionTab.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  searchValue: PropTypes.string,
  config: PropTypes.instanceOf(Object),
  user: PropTypes.instanceOf(Object).isRequired,
  isLocked: PropTypes.bool,
};

export default SectionTab;
